/**
 * TGQuickSearch
 * Global search bar that filters across all visible columns
 */

import React, { useState, useCallback } from 'react';
import { Input } from 'antd';
import { SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { spacing } from '../../../theme/designTokens';

/**
 * @param {Object} props
 * @param {string} [props.value] - Current search value
 * @param {Function} props.onChange - (value: string) => void
 * @param {string} [props.placeholder] - Placeholder text
 * @param {number} [props.debounceMs] - Debounce delay in ms (default: 300)
 */
function TGQuickSearch({
  value = '',
  onChange,
  placeholder = 'Search across all columns...',
  debounceMs = 300,
}) {
  const [localValue, setLocalValue] = useState(value);

  // Debounced change handler
  const debouncedChange = useCallback(
    debounce((val) => {
      onChange(val);
    }, debounceMs),
    [onChange, debounceMs]
  );

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedChange(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Bypass debounce on Enter
      debouncedChange.cancel();
      onChange(localValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
      e.target.blur();
    }
  };

  return (
    <div
      style={{
        marginBottom: spacing.md,
        maxWidth: 500,
      }}
    >
      <Input
        size="large"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
        suffix={
          localValue ? (
            <CloseCircleOutlined
              onClick={handleClear}
              style={{ color: 'rgba(0,0,0,0.45)', cursor: 'pointer' }}
            />
          ) : null
        }
        aria-label="Quick search across all columns"
        title="Press Enter to search immediately, Esc to clear"
        allowClear
      />
    </div>
  );
}

export default TGQuickSearch;
